import React, { useMemo } from 'react';

import { Tracking, TrackingLabels, TrackingStage } from './types';
import { currentSubStage, formatShortDate, layoutStages } from './utils';
import styles from './styles.module.scss';

const PALETTE_SIZE = 4;
// Near the bar's ends the marker label stacks on two lines and is anchored inwards.
const MARKER_EDGE_PCT = 12;
// Stages narrower than this share of the bar use the design's small label (e.g. CONCLUIDO).
const NARROW_STAGE_PCT = 8;

export const DEFAULT_LABELS: TrackingLabels = {
  totalProgress: 'Avance total',
  subStage: 'Subetapa',
  stages: 'Etapas',
  subStages: 'Sub etapas',
  inProgressSince: date => `En curso desde el ${date}`,
  startedOn: date => `Iniciada el ${date}`,
  expectedEnd: date => `Finalización esperada: ${date}`,
  summary: (progress, stage) => `Avance total ${progress}. Etapa actual: ${stage}.`
};

interface Props {
  tracking: Tracking;
  /** Single thin bar for listings and popups: no ruler, sub-stages or dates. */
  compact?: boolean;
  showMarker?: boolean;
  className?: string;
  /** Texts to show; anything left out uses the Spanish defaults. */
  labels?: Partial<TrackingLabels>;
}

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

const formatPct = (value: number) => `${Math.round(value * 10) / 10}%`;

const dateTooltip = (stage: TrackingStage, labels: TrackingLabels) => {
  const date = formatShortDate(stage.startDate);
  const started = stage.progress >= 100 ? labels.startedOn(date) : labels.inProgressSince(date);
  return stage.expectedEndDate ? `${started}\n${labels.expectedEnd(formatShortDate(stage.expectedEndDate))}` : started;
};

const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true" focusable="false">
    <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" fill="none" stroke="currentColor" />
    <path d="M1.5 6h13M5 1v3M11 1v3" stroke="currentColor" />
    <path d="M4 8.5h2v2H4zM7 8.5h2v2H7zM10 8.5h2v2h-2z" fill="currentColor" />
  </svg>
);

/** Weighted stage timeline of a project's Seguimiento. Presentational: it fills its container. */
function TrackingTimeline({ tracking, compact = false, showMarker = true, className, labels }: Props) {
  const text = useMemo(() => ({ ...DEFAULT_LABELS, ...labels }), [labels]);
  const layout = useMemo(() => layoutStages(tracking.stages), [tracking.stages]);
  const subStage = useMemo(
    // Older payloads have no currentSubStage: fall back to inferring it.
    () => currentSubStage(tracking.stages, tracking.currentStage, tracking.currentSubStage || null),
    [tracking]
  );
  const current = tracking.stages.find(stage => stage.id === tracking.currentStage);
  const total = tracking.totalProgress;
  const markerEdge = total < MARKER_EDGE_PCT ? styles.markerStart : total > 100 - MARKER_EDGE_PCT ? styles.markerEnd : null;

  return (
    <div
      className={cx(styles.timeline, compact && styles.compact, className)}
      role="group"
      aria-label={text.summary(formatPct(total), current ? current.name : '-')}
    >
      {!compact && (
        <div className={styles.rowLabels} aria-hidden="true">
          <span className={styles.stagesLabel}>{text.stages}</span>
          <span className={styles.subStagesLabel}>{text.subStages}</span>
        </div>
      )}
      <div className={styles.track}>
        {showMarker && (
          <div className={styles.markerRow}>
            <div className={cx(styles.marker, markerEdge)} style={{ left: `${total}%` }}>
              <span className={styles.markerLabel}>{text.totalProgress}</span>
              <span className={styles.markerValue}>{formatPct(total)}</span>
              {/* As in the design, the sub-stage is named only away from the ends. */}
              {subStage && !compact && !markerEdge && (
                <span className={styles.markerSubStage}>
                  {text.subStage}: {subStage.name}
                </span>
              )}
            </div>
            <span className={styles.markerArrow} style={{ left: `${total}%` }} aria-hidden="true" />
          </div>
        )}
        {!compact && <div className={styles.ruler} aria-hidden="true" />}
        <div className={styles.bar}>
          {layout.map(({ stage, widthPct, colorIndex }) => (
            <div
              key={stage.id}
              className={cx(
                styles.stage,
                styles[`palette${colorIndex % PALETTE_SIZE}`],
                widthPct < NARROW_STAGE_PCT && styles.narrow
              )}
              style={{ width: `${widthPct}%` }}
              title={stage.name}
            >
              <div className={styles.stageHeader}>
                <span className={styles.fill} style={{ width: `${stage.progress}%` }} />
                <span className={styles.stageName}>{stage.name}</span>
              </div>
              {!compact && (
                <div className={styles.subStages}>
                  <span className={styles.fill} style={{ width: `${stage.progress}%` }} />
                  {stage.subStages.length ? (
                    stage.subStages.map(item => (
                      <span
                        key={item.id}
                        className={styles.subStage}
                        tabIndex={0}
                        aria-label={item.name}
                        data-tooltip={item.name}
                      />
                    ))
                  ) : (
                    <span className={styles.subStage} />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        {!compact && (
          <div className={styles.dates}>
            {layout
              .filter(({ stage }) => stage.startDate)
              .map(({ stage, startPct }) => (
                <span
                  key={stage.id}
                  className={styles.date}
                  style={{ left: `${startPct}%` }}
                  tabIndex={0}
                  aria-label={dateTooltip(stage, text).replace('\n', '. ')}
                  data-tooltip={dateTooltip(stage, text)}
                >
                  <CalendarIcon />
                </span>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TrackingTimeline;
